/**
 * Node Data Attributes Section
 * PROJ-042 Phase 2 - Data Attributes for Canvas Nodes
 *
 * Allows users to:
 * - Select data attributes (email, phone, SSN, etc.) for a workflow node
 * - Assign/override classification levels per attribute
 * - Remove attributes from the node
 */

import { useState, useEffect, useMemo } from 'react';
import { Database, Plus, X, ChevronDown, Loader2 } from 'lucide-react';
import { CollapsibleSection } from '@/components/ui/collapsible-section';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import {
  getDataAttributes,
  getNodeDataAttributes,
  addNodeDataAttribute,
  updateNodeDataAttribute,
  removeNodeDataAttribute,
} from '@/lib/dataAttributesApi';
import { getDataClassifications } from '@/lib/dataClassificationApi';
import type { DataAttribute, NodeDataAttribute } from '@/types/data-attributes';
import type { DataClassification } from '@/types/data-classification';
import { formatDataAttributeCategory } from '@/types/data-attributes';

interface NodeDataAttributesSectionProps {
  nodeId: string;
  /** Called when attributes change - passes array of attribute codes */
  onAttributesChange?: (attributeCodes: string[]) => void;
}

// Helper to check if a string is a valid UUID
const isValidUUID = (id: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(id);
};

export function NodeDataAttributesSection({
  nodeId,
  onAttributesChange,
}: NodeDataAttributesSectionProps) {
  // State
  const [allAttributes, setAllAttributes] = useState<DataAttribute[]>([]);
  const [nodeAttributes, setNodeAttributes] = useState<NodeDataAttribute[]>([]);
  const [classifications, setClassifications] = useState<DataClassification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddingAttribute, setIsAddingAttribute] = useState(false);
  const [addPopoverOpen, setAddPopoverOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if node has been persisted to database (has valid UUID)
  const isPersistedNode = isValidUUID(nodeId);

  // Load initial data
  useEffect(() => {
    async function loadData() {
      setIsLoading(true);
      setError(null);

      try {
        // Only fetch node-specific attributes if the node is persisted (has valid UUID)
        const [attrs, nodeAttrs, classifs] = await Promise.all([
          getDataAttributes(),
          isPersistedNode ? getNodeDataAttributes(nodeId) : Promise.resolve([]),
          getDataClassifications(),
        ]);

        setAllAttributes(attrs);
        setNodeAttributes(nodeAttrs);
        setClassifications(classifs);
      } catch (err) {
        console.error('Error loading data attributes:', err);
        setError('Error al cargar atributos');
      } finally {
        setIsLoading(false);
      }
    }

    loadData();
  }, [nodeId, isPersistedNode]);

  // Available attributes (not already selected)
  const availableAttributes = useMemo(() => {
    const selectedCodes = new Set(nodeAttributes.map((na) => na.dataAttributeCode));
    return allAttributes.filter((attr) => !selectedCodes.has(attr.code));
  }, [allAttributes, nodeAttributes]);

  // Group attributes by category
  const groupedAvailableAttributes = useMemo(() => {
    const groups: Record<string, DataAttribute[]> = {};
    availableAttributes.forEach((attr) => {
      const cat = attr.category || 'OTHER';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(attr);
    });
    return groups;
  }, [availableAttributes]);

  // Handle adding an attribute
  const handleAddAttribute = async (attributeCode: string) => {
    if (!nodeId || isAddingAttribute) return;

    setIsAddingAttribute(true);
    try {
      const attr = allAttributes.find((a) => a.code === attributeCode);
      const newAttr = await addNodeDataAttribute(
        nodeId,
        attributeCode,
        attr?.defaultClassificationCode || undefined
      );

      setNodeAttributes((prev) => [...prev, newAttr]);
      onAttributesChange?.(
        [...nodeAttributes, newAttr].map((na) => na.dataAttributeCode)
      );
      setAddPopoverOpen(false);
    } catch (err) {
      console.error('Error adding attribute:', err);
    } finally {
      setIsAddingAttribute(false);
    }
  };

  // Handle changing classification for an attribute
  const handleClassificationChange = async (
    attributeCode: string,
    classificationCode: string
  ) => {
    if (!nodeId) return;

    try {
      const updated = await updateNodeDataAttribute(
        nodeId,
        attributeCode,
        classificationCode
      );

      setNodeAttributes((prev) =>
        prev.map((na) =>
          na.dataAttributeCode === attributeCode ? updated : na
        )
      );
    } catch (err) {
      console.error('Error updating classification:', err);
    }
  };

  // Handle removing an attribute
  const handleRemoveAttribute = async (attributeCode: string) => {
    if (!nodeId) return;

    try {
      await removeNodeDataAttribute(nodeId, attributeCode);
      setNodeAttributes((prev) =>
        prev.filter((na) => na.dataAttributeCode !== attributeCode)
      );
      onAttributesChange?.(
        nodeAttributes
          .filter((na) => na.dataAttributeCode !== attributeCode)
          .map((na) => na.dataAttributeCode)
      );
    } catch (err) {
      console.error('Error removing attribute:', err);
    }
  };

  // Get classification color
  const getClassificationColor = (colorCode: string) => {
    return { backgroundColor: colorCode };
  };

  return (
    <CollapsibleSection
      id={`${nodeId}-data-attributes`}
      title="Atributos de Datos"
      icon={Database}
      defaultOpen={false}
      badge={
        nodeAttributes.length > 0 ? (
          <Badge variant="secondary" className="text-xs px-1.5 py-0">
            {nodeAttributes.length}
          </Badge>
        ) : null
      }
    >
      <div className="space-y-3">
        {/* Error State */}
        {error && (
          <p className="text-xs text-destructive">{error}</p>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            Cargando atributos...
          </div>
        )}

        {/* Selected Attributes List */}
        {!isLoading && nodeAttributes.length > 0 && (
          <div className="space-y-2">
            {nodeAttributes.map((nodeAttr) => (
              <div
                key={nodeAttr.dataAttributeCode}
                className="flex items-center gap-2 p-2 rounded-md border bg-muted/30"
              >
                {/* Attribute Icon & Name */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    {nodeAttr.dataAttribute.icon && (
                      <span className="text-sm">{nodeAttr.dataAttribute.icon}</span>
                    )}
                    <span className="text-sm font-medium truncate">
                      {nodeAttr.dataAttribute.name}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {formatDataAttributeCategory(nodeAttr.dataAttribute.category)}
                  </p>
                </div>

                {/* Classification Selector */}
                <Select
                  value={nodeAttr.classificationCode}
                  onValueChange={(value) =>
                    handleClassificationChange(nodeAttr.dataAttributeCode, value)
                  }
                >
                  <SelectTrigger className="h-7 w-28 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {classifications.map((cls) => (
                      <SelectItem
                        key={cls.code}
                        value={cls.code}
                        className="text-xs"
                      >
                        <div className="flex items-center gap-1.5">
                          <span
                            className="h-2 w-2 rounded-full"
                            style={getClassificationColor(cls.colorCode)}
                          />
                          {cls.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Remove Button */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-muted-foreground hover:text-destructive"
                  onClick={() => handleRemoveAttribute(nodeAttr.dataAttributeCode)}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && nodeAttributes.length === 0 && isPersistedNode && (
          <p className="text-xs text-muted-foreground text-center py-2">
            No hay atributos asignados a este nodo
          </p>
        )}

        {/* Unsaved Node Warning */}
        {!isLoading && !isPersistedNode && (
          <div className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-950/20 dark:text-amber-400 p-2 rounded-md text-center">
            Guarda el workflow para asignar atributos de datos a este nodo.
          </div>
        )}

        {/* Add Attribute Button - Only show for persisted nodes */}
        {!isLoading && availableAttributes.length > 0 && isPersistedNode && (
          <Popover open={addPopoverOpen} onOpenChange={setAddPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="w-full h-8 text-xs justify-start"
                disabled={isAddingAttribute}
              >
                {isAddingAttribute ? (
                  <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                ) : (
                  <Plus className="h-3 w-3 mr-2" />
                )}
                Agregar atributo
                <ChevronDown className="h-3 w-3 ml-auto" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[280px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Buscar atributo..." className="h-8" />
                <CommandList>
                  <CommandEmpty>No se encontraron atributos</CommandEmpty>
                  <ScrollArea className="max-h-[250px]">
                    {Object.entries(groupedAvailableAttributes).map(
                      ([category, attrs]) => (
                        <CommandGroup
                          key={category}
                          heading={formatDataAttributeCategory(category as any)}
                        >
                          {attrs.map((attr) => (
                            <CommandItem
                              key={attr.code}
                              value={`${attr.name} ${attr.code}`}
                              onSelect={() => handleAddAttribute(attr.code)}
                              className="text-xs cursor-pointer"
                            >
                              <div className="flex items-center gap-2 w-full">
                                {attr.icon && (
                                  <span className="text-sm">{attr.icon}</span>
                                )}
                                <span className="flex-1">{attr.name}</span>
                                {attr.defaultClassification && (
                                  <span
                                    className="h-2 w-2 rounded-full"
                                    style={getClassificationColor(
                                      attr.defaultClassification.colorCode
                                    )}
                                    title={attr.defaultClassification.name}
                                  />
                                )}
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )
                    )}
                  </ScrollArea>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}

        {/* Help Text */}
        {isPersistedNode && (
          <p className="text-xs text-muted-foreground">
            Selecciona los datos que procesa este nodo para cumplimiento GDPR/LGPD.
          </p>
        )}
      </div>
    </CollapsibleSection>
  );
}
